import type { CreateOrderQuoteOfferBatchSampleInput } from '@codezo/graphql';
import {
	OrderAdjustmentCalculationType,
	OrderAdjustmentType,
	SamplePaymentTreatment,
	useGetProductPricesQuery,
	useSuggestOrderQuoteItemPricingQuery,
} from '@codezo/graphql';
import { Box } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { Button } from '../../../ui/button';
import { DialogBody, DialogFooter } from '../../../ui/dialog';
import { ProductImage } from '../../../ui/product-image';
import { VetterSelector } from '../../../selectors/VetterSelector';
import { cn } from '../../../../lib/utils';
import { useCurrencyStore } from '../../../../stores/currency';
import type { AdjustmentInput, PricingValues, QuoteItem, SampleInput } from './types';
import { isUnpriced } from '../../../pricing/tier-pricing';
import { emptyPricing, primaryImageObject } from './types';
import { AdjustmentsTab } from './AdjustmentsTab';
import { SampleTab } from './SampleTab';
import type { CatalogPriceRung } from '../../../pricing/catalog-types';
import {
	appliedRung,
	catalogueFill,
	inFormCurrency,
	useCatalogSuggestions,
} from '../../../pricing/catalog-suggestions';
import { CatalogCoverageBanner } from '../../../pricing/CatalogCoverageBanner';
import {
	bestSampleRow,
	sampleRowFill,
	useCatalogSamples,
	type SampleFill,
} from '../../../pricing/SampleCatalogPicker';
import { PriceRowCard } from '../../../pricing/PriceRowCard';
import { TierFeasibility } from '../../../pricing/TierFeasibility';
import {
	applyVetterSuggestion,
	isVetterSuggestion,
	type PricingSuggestion,
} from './vetter-prefill';
import { VetterQuoteBanner, VetterTierHint } from './VetterQuoteHints';

/* ── Props ── */

interface WizardPricingStepProps {
	quoteItems: QuoteItem[];
	selectedItems: Set<string>;
	/** Quote-level deadline; per-item deadline takes precedence for feasibility. */
	quoteDeadline?: string | null;
	/** Offer accept-by date (worst-case anchor) from step 1, as an ISO string. */
	acceptDeadline?: string | null;
	pricing: Record<string, Record<string, PricingValues>>;
	setPricing: React.Dispatch<React.SetStateAction<Record<string, Record<string, PricingValues>>>>;
	vetters: Record<string, string>;
	setVetters: React.Dispatch<React.SetStateAction<Record<string, string>>>;
	adjustments: Record<string, AdjustmentInput[]>;
	setAdjustments: React.Dispatch<React.SetStateAction<Record<string, AdjustmentInput[]>>>;
	samples: Record<string, SampleInput>;
	setSamples: React.Dispatch<React.SetStateAction<Record<string, SampleInput>>>;
	customQuantities: Record<string, Array<{ id: string; quantity: number }>>;
	activeItemId: string | null;
	setActiveItemId: (id: string | null) => void;
	activeQuantityId: string | null;
	setActiveQuantityId: (id: string | null) => void;
	onBack: () => void;
	onNext: () => void;
}

/* ── Helper: get all quantities (original + custom) for an item ── */

function allQuantities(
	quoteItems: QuoteItem[],
	customQuantities: Record<string, Array<{ id: string; quantity: number }>>,
	itemId: string
) {
	const item = quoteItems.find((i) => i.id === itemId);
	return [...(item?.quantities ?? []), ...(customQuantities[itemId] ?? [])];
}

/* ── Component ── */

export function WizardPricingStep({
	quoteItems,
	selectedItems,
	quoteDeadline,
	acceptDeadline,
	pricing,
	setPricing,
	vetters,
	setVetters,
	adjustments,
	setAdjustments,
	samples,
	setSamples,
	customQuantities,
	activeItemId,
	setActiveItemId,
	activeQuantityId,
	setActiveQuantityId,
	onBack,
	onNext,
}: WizardPricingStepProps) {
	const { selectedCurrency: currency } = useCurrencyStore();

	const selectedArr = Array.from(selectedItems);
	const activeItem = quoteItems.find((i) => i.id === activeItemId) ?? null;

	/**
	 * What the catalogue would charge for the line being priced.
	 *
	 * Keyed on the QUOTE LINE, not an offer — at this step no offer exists yet.
	 * The supplier comes from the Assign vetter control beside it; before one is
	 * chosen it is omitted, and the no-supplier prices come back, which is the
	 * right answer at that point.
	 *
	 * An unreachable catalogue costs a prefill, never a quote, so a failure here
	 * is ignored rather than surfaced.
	 */
	const { data: suggestionData } = useSuggestOrderQuoteItemPricingQuery({
		variables: {
			orderQuoteItemId: activeItemId ?? '',
			vetterEntityId: (activeItemId && vetters[activeItemId]) || undefined,
			// Every suggestion comes back restated in the currency this form saves in.
			inCurrency: currency,
		},
		skip: !activeItemId,
		fetchPolicy: 'no-cache',
	});
	const suggestionList = useMemo(
		() => suggestionData?.suggestOrderQuoteItemPricing ?? [],
		[suggestionData]
	);

	/*
	 * EVERY catalogue price on the product — the no-supplier ones and every
	 * supplier's — rather than only the ladder the lookup resolved.
	 *
	 * The suggestion endpoint deliberately returns one source, so a supplier's
	 * prices were invisible until they were assigned, which read as missing data.
	 * The assignment still decides what is USABLE; it should not decide what is
	 * visible.
	 */
	const { data: catalogueData } = useGetProductPricesQuery({
		variables: { input: { productId: activeItem?.productId ?? '' }, currency },
		skip: !activeItem?.productId,
		fetchPolicy: 'no-cache',
	});
	const cataloguePrices = catalogueData?.getProductPrices ?? [];

	const assignedVetterId = activeItemId ? (vetters[activeItemId] ?? null) : null;

	const { suggestions, optionsFor, exactFills } = useCatalogSuggestions({
		list: suggestionList,
		cataloguePrices,
		assignedVetterId,
		formCurrency: currency,
	});

	/**
	 * An exact match needs no confirming, so it fills itself.
	 *
	 * "Exact" means the volume AND the source line up: with no supplier chosen,
	 * the no-supplier row for this quantity; with one chosen, that supplier's own
	 * row for it. A supplier's price falling back to the no-supplier ladder is
	 * NOT an exact match — the catalogue reached for it, which is a suggestion,
	 * not an answer.
	 *
	 * Only untouched tiers, and ones a previous auto-fill wrote: a figure the
	 * admin typed is never overwritten, but one this rule chose is re-resolved
	 * when the supplier changes under it.
	 */
	useEffect(() => {
		if (!activeItemId || exactFills.length === 0) return;
		setPricing((prev) => {
			const item = { ...(prev[activeItemId] || {}) };
			let changed = false;
			for (const { quantityId, fill } of exactFills) {
				const row = item[quantityId] || emptyPricing;
				if (row.touched && !row.autoApplied) continue;
				const { cost, ...rest } = fill;
				const next = { ...row, ...rest, price: cost, touched: true, autoApplied: true };
				if (
					row.price === next.price &&
					row.markup === next.markup &&
					row.salePrice === next.salePrice &&
					row.autoApplied
				) {
					continue;
				}
				item[quantityId] = next;
				changed = true;
			}
			// Returning `prev` unchanged is what stops this re-running forever.
			return changed ? { ...prev, [activeItemId]: item } : prev;
		});
	}, [exactFills, activeItemId, setPricing]);

	/** Fills every tier that still has an untouched suggestion. */
	const applyAllSuggestions = () => {
		if (!activeItemId) return;
		for (const q of pendingCatalogue) {
			const sug = suggestions.get(q.id);
			if (sug) applySuggestion(activeItemId, q.id, appliedRung(sug));
		}
	};

	/**
	 * Fills a tier from ONE rung of the ladder — the applied break or any other.
	 * Nothing is written until the admin saves.
	 */
	const applySuggestion = (itemId: string, quantityId: string, rung: CatalogPriceRung) => {
		setPricing((prev) => {
			const row = prev[itemId]?.[quantityId] || emptyPricing;
			const fill = catalogueFill(rung, currency, row);
			// No figure in this form's currency (no rate for the pair): nothing to fill.
			if (!fill) return prev;
			const { cost, ...rest } = fill;
			return {
				...prev,
				[itemId]: {
					...(prev[itemId] || {}),
					[quantityId]: {
						...row,
						...rest,
						price: cost,
						touched: true,
						autoApplied: false,
						vetterQuote: null,
					},
				},
			};
		});
	};

	/**
	 * Fills a tier from the supplier's own price request — cost, production days
	 * and their shipping. Never the sale price: that is the admin's to set.
	 */
	const applyVetter = (itemId: string, quantityId: string, sug: PricingSuggestion) => {
		setPricing((prev) => {
			const next = applyVetterSuggestion(
				prev[itemId]?.[quantityId] || emptyPricing,
				sug,
				currency
			);
			if (!next) return prev;
			return { ...prev, [itemId]: { ...(prev[itemId] || {}), [quantityId]: next } };
		});
	};

	const applyAllVetter = () => {
		if (!activeItemId) return;
		for (const q of pendingVetter) {
			const sug = suggestions.get(q.id);
			if (sug) applyVetter(activeItemId, q.id, sug);
		}
	};

	const getItemQuantities = (itemId: string) =>
		allQuantities(quoteItems, customQuantities, itemId);

	/** Distinct catalogue breaks for this line, ascending — the banner's range. */
	const catalogueBreaks = [...new Set(cataloguePrices.map((r) => r.quantity))].sort(
		(a, b) => a - b
	);

	/** Tiers carrying a suggestion the admin has not acted on yet. */
	const pendingSuggestions = activeItemId
		? getItemQuantities(activeItemId).filter(
				(q) => suggestions.has(q.id) && !pricing[activeItemId]?.[q.id]?.touched
			)
		: [];
	/* Split by where the answer came from: the catalogue's banner speaks for its
	   own breaks, and a supplier's price request gets its own — it is a cost with
	   no margin, and saying "catalogue" over it would misname the source. */
	const isVetterTier = (quantityId: string) => {
		const sug = suggestions.get(quantityId);
		return !!sug && isVetterSuggestion(sug);
	};
	const pendingCatalogue = pendingSuggestions.filter((q) => !isVetterTier(q.id));
	// A tier the catalogue auto-filled is still open to the supplier's own
	// answer — only figures a person entered are left alone.
	const pendingVetter = activeItemId
		? getItemQuantities(activeItemId).filter((q) => {
				const row = pricing[activeItemId]?.[q.id];
				return isVetterTier(q.id) && (!row?.touched || row.autoApplied);
			})
		: [];
	const vetterAnswers = [...suggestions.values()].filter(isVetterSuggestion);

	/* Named by the catalogue itself now: the suggestion carries the supplier whose
	   row answered, so nothing here has to infer it from the assignment. */

	/* Across every selected product, not just the one on screen: the footer is
	   about the step, and the step is not done while another product is empty. */
	const allTiers = selectedArr.flatMap((itemId) =>
		getItemQuantities(itemId).map((q) => pricing[itemId]?.[q.id])
	);
	const totalTierCount = allTiers.length;
	const pricedTierCount = allTiers.filter(
		(v) => v && parseFloat(v.price) > 0 && parseFloat(v.salePrice) > 0
	).length;

	/*
	 * Tiers carrying a cost but no client price. They save fine; the backend only
	 * refuses to SEND a batch containing one, so the footer points them out.
	 */
	const unpricedTiers = selectedArr.flatMap((itemId) =>
		Object.entries(pricing[itemId] ?? {})
			.filter(([, v]) =>
				isUnpriced({ cost: v.price, salePrice: v.salePrice, markup: v.markup })
			)
			.map(([quantityId]) => ({ itemId, quantityId }))
	);

	const pricedCount = (itemId: string) => {
		const ip = pricing[itemId];
		if (!ip) return 0;
		return Object.values(ip).filter((v) => v.price && parseFloat(v.price) > 0).length;
	};

	/**
	 * Every catalogue sample price on the product, in the form's currency — the
	 * default one and each supplier's. The picker shows them all; the assigned
	 * supplier decides which are usable and which is the best match.
	 */
	const catalogSamples = useCatalogSamples(activeItem?.productId, currency);

	/**
	 * The sample with a catalogue price's figures. Only what the catalogue states
	 * is written: a row without shipping or days leaves the ones the admin typed,
	 * the same as the sample modal.
	 */
	const applySampleFill = (fill: SampleFill) =>
		updateSample({
			baseSamplePrice: fill.baseSamplePrice,
			...(fill.baseShippingPrice != null && { baseShippingPrice: fill.baseShippingPrice }),
			...(fill.minProductionDays != null && { minProductionDays: fill.minProductionDays }),
			...(fill.maxProductionDays != null && { maxProductionDays: fill.maxProductionDays }),
			...(fill.deliveryDaysMin != null && { deliveryDaysMin: fill.deliveryDaysMin }),
			...(fill.deliveryDaysMax != null && { deliveryDaysMax: fill.deliveryDaysMax }),
			currency,
		});

	/**
	 * Opening the Sample tab on a product with no sample yet fills it from the
	 * best match: the assigned supplier's own sample price, else the default. Only
	 * on opening, and only an empty sample — a sample is optional, so filling one
	 * on every product would offer samples nobody asked for, and a figure the admin
	 * typed is never replaced.
	 */
	useEffect(() => {
		if (activeQuantityId !== '__sample__' || !activeItemId) return;
		if (samples[activeItemId]) return;
		const best = bestSampleRow(catalogSamples, vetters[activeItemId]);
		const fill = best ? sampleRowFill(best, currency) : null;
		if (fill) applySampleFill(fill);
		// Keyed on the rows' ids: the array is rebuilt every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeQuantityId, activeItemId, catalogSamples.map((r) => r.id).join()]);

	const updateSample = (fields: Partial<CreateOrderQuoteOfferBatchSampleInput>) => {
		if (!activeItemId) return;
		setSamples((prev) => ({
			...prev,
			[activeItemId]: {
				...(prev[activeItemId] || {
					baseSamplePrice: 0,
					currency,
					// Seeded, not just defaulted in the picker's display: the input is
					// submitted as-is, so an absent value would take the backend
					// default rather than what the admin sees selected.
					paymentTreatment: SamplePaymentTreatment.Prepaid,
				}),
				...fields,
			},
		}));
	};

	const addAdjustment = () => {
		if (!activeItemId) return;
		setAdjustments((prev) => ({
			...prev,
			[activeItemId]: [
				...(prev[activeItemId] ?? []),
				{
					name: '',
					type: OrderAdjustmentType.Fee,
					calculationType: OrderAdjustmentCalculationType.Percentage,
					value: 0,
				},
			],
		}));
	};

	const updateAdjustment = (idx: number, patch: Partial<AdjustmentInput>) => {
		if (!activeItemId) return;
		const next = [...(adjustments[activeItemId] ?? [])];
		next[idx] = { ...next[idx], ...patch };
		setAdjustments((p) => ({ ...p, [activeItemId]: next }));
	};

	const removeAdjustment = (idx: number) => {
		if (!activeItemId) return;
		const next = (adjustments[activeItemId] ?? []).filter((_, i) => i !== idx);
		setAdjustments((p) => ({ ...p, [activeItemId]: next }));
	};

	return (
		<div
			key="pricing"
			className="flex min-h-0 flex-1 animate-in fade-in-0 slide-in-from-right-2 flex-col duration-200"
		>
			<DialogBody className="p-0">
				{/* Sizes to its content — the 480px floor was holding room for the
				    deliveries that now live in their own step — but capped, because the
				    step is wrapped in AnimatedHeight and that sets a literal pixel
				    height from the measured content. Uncapped, a long tab (the Sample
				    one) made that height taller than the viewport, which pushed the
				    footer off screen with nothing scrolling: the buttons went out of
				    reach. The cap is what gives the tab body below something to
				    scroll inside. */}
				<div className="flex max-h-[65vh]">
					{/* Sidebar */}
					<div className="scroll-thin flex w-44 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-line-1 p-2">
						{selectedArr.map((itemId) => {
							const item = quoteItems.find((i) => i.id === itemId);
							if (!item) return null;
							const isActive = itemId === activeItemId;
							const img = primaryImageObject(item);
							const priced = pricedCount(itemId);
							const total = getItemQuantities(itemId).length;
							return (
								<button
									key={itemId}
									type="button"
									onClick={() => {
										setActiveItemId(itemId);
										const itm = quoteItems.find((i) => i.id === itemId);
										setActiveQuantityId(itm?.quantities[0]?.id ?? null);
									}}
									className={cn(
										'flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
										isActive
											? 'bg-accent-soft ring-1 ring-accent-line'
											: 'hover:bg-surface-hover'
									)}
								>
									{img?.url ? (
										<ProductImage
											image={img}
											alt={item.productName || ''}
											className="h-9 w-9 shrink-0 rounded-[var(--r-2)] border border-line-1"
										/>
									) : (
										<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill">
											<Box size={14} className="text-text-4" />
										</div>
									)}
									<div className="min-w-0 flex-1">
										<div className="truncate text-sm font-medium text-text-1">
											{item.productName || 'Unknown'}
										</div>
										<div className="text-2xs text-text-4">
											{priced}/{total} priced
											{(adjustments[itemId]?.length ?? 0) > 0 && (
												<span> · {adjustments[itemId].length} adj.</span>
											)}
										</div>
									</div>
								</button>
							);
						})}
					</div>

					{/* Content */}
					<div className="flex flex-1 flex-col overflow-hidden">
						{activeItem ? (
							<>
								{/* Product header */}
								<div className="flex items-center gap-3 border-b border-line-1 px-4 py-3">
									{primaryImageObject(activeItem)?.url ? (
										<ProductImage
											image={primaryImageObject(activeItem)!}
											alt={activeItem.productName || ''}
											className="h-10 w-10 shrink-0 rounded-lg border border-line-1"
										/>
									) : (
										<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line-1 bg-surface-fill">
											<Box size={16} className="text-text-4" />
										</div>
									)}
									<div className="min-w-0 flex-1">
										<div className="text-base font-medium text-text-1">
											{activeItem.productName || 'Unknown'}
										</div>
										{activeItem.productSku && (
											<div className="font-mono text-xs text-text-4">
												{activeItem.productSku}
											</div>
										)}
									</div>
									<div className="w-44 shrink-0">
										<VetterSelector
											value={vetters[activeItemId!] || ''}
											onChange={(val) =>
												setVetters((prev) => ({
													...prev,
													[activeItemId!]: val,
												}))
											}
											placeholder="Assign vetter"
											clearable
											creatable
										/>
									</div>
								</div>

								{/* One tab group. Pricing used to sit alone on the left with
								    Adjustments and Sample floated right, which read as two
								    unrelated controls rather than three tabs. */}
								<div className="flex items-center border-b border-line-1 px-4">
									{/* One tab, not one per quantity: the tiers are a table now,
									    and tabbing between them was the thing that made comparing
									    them impossible. */}
									<button
										type="button"
										onClick={() =>
											setActiveQuantityId(
												getItemQuantities(activeItemId!)[0]?.id ?? null
											)
										}
										className={cn(
											'relative px-3 py-2 text-sm font-medium transition-colors',
											!activeQuantityId?.startsWith('__')
												? 'text-text-1'
												: 'text-text-4 hover:text-text-2'
										)}
									>
										Pricing
										{!activeQuantityId?.startsWith('__') && (
											<div className="absolute bottom-0 left-3 right-3 h-[1.5px] bg-text-1" />
										)}
									</button>
									<div className="flex items-center">
										<button
											type="button"
											onClick={() => setActiveQuantityId('__adjustments__')}
											className={cn(
												'relative flex items-center gap-1.5 px-3 py-2 text-sm transition-colors',
												activeQuantityId === '__adjustments__'
													? 'text-text-1'
													: 'text-text-4 hover:text-text-2'
											)}
										>
											Adjustments
											<span
												className={cn(
													'min-w-4 rounded-full px-1.5 text-center text-2xs font-medium',
													(adjustments[activeItemId!]?.length ?? 0) > 0
														? 'bg-surface-input text-text-2'
														: 'bg-surface-fill text-text-4'
												)}
											>
												{adjustments[activeItemId!]?.length ?? 0}
											</span>
											{activeQuantityId === '__adjustments__' && (
												<div className="absolute bottom-0 left-3 right-3 h-[1.5px] bg-text-1" />
											)}
										</button>
										<button
											type="button"
											onClick={() => setActiveQuantityId('__sample__')}
											className={cn(
												'relative flex items-center gap-1.5 px-3 py-2 text-sm transition-colors',
												activeQuantityId === '__sample__'
													? 'text-text-1'
													: 'text-text-4 hover:text-text-2'
											)}
										>
											Sample
											{samples[activeItemId!]?.baseSamplePrice != null &&
												samples[activeItemId!].baseSamplePrice > 0 && (
													<span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
												)}
											{activeQuantityId === '__sample__' && (
												<div className="absolute bottom-0 left-3 right-3 h-[1.5px] bg-text-1" />
											)}
										</button>
									</div>
								</div>

								{/* Tab content */}
								<div className="scroll-thin flex-1 overflow-y-auto p-4">
									{/* Adjustments tab */}
									{activeQuantityId === '__adjustments__' && (
										<AdjustmentsTab
											activeItemId={activeItemId!}
											adjustments={adjustments}
											addAdjustment={addAdjustment}
											updateAdjustment={updateAdjustment}
											removeAdjustment={removeAdjustment}
											currency={currency}
										/>
									)}

									{/* Sample tab */}
									{activeQuantityId === '__sample__' &&
										(() => {
											const s = samples[activeItemId!];
											return (
												<SampleTab
													sample={s}
													updateSample={updateSample}
													currency={currency}
													catalogSamples={catalogSamples}
													assignedVetterId={
														vetters[activeItemId!] ?? null
													}
													onPickCatalog={applySampleFill}
												/>
											);
										})()}

									{/* One card per quantity, per the Pricing step design: the
									    figures on one line, the consequence opposite the
									    quantity, and where the numbers came from stated beside
									    it. */}
									{activeQuantityId && !activeQuantityId.startsWith('__') && (
										<div className="flex flex-col gap-2 px-3 py-2.5">
											{vetterAnswers.length > 0 && (
												<VetterQuoteBanner
													supplierName={
														vetterAnswers[0].vetterEntity?.name ??
														'the supplier'
													}
													supplierCurrency={vetterAnswers[0].currency}
													offerCurrency={currency}
													convertible={
														!!inFormCurrency(vetterAnswers[0], currency)
													}
													note={vetterAnswers[0].note}
													answeredCount={vetterAnswers.length}
													pendingCount={pendingVetter.length}
													tierCount={
														getItemQuantities(activeItemId!).length
													}
													onApplyAll={applyAllVetter}
												/>
											)}
											<CatalogCoverageBanner
												priceCount={catalogueBreaks.length}
												lowestQuantity={catalogueBreaks[0] ?? 0}
												highestQuantity={
													catalogueBreaks[catalogueBreaks.length - 1] ?? 0
												}
												pendingCount={pendingCatalogue.length}
												tierCount={getItemQuantities(activeItemId!).length}
												hasSupplier={!!vetters[activeItemId!]}
												onApplyAll={applyAllSuggestions}
											/>

											{getItemQuantities(activeItemId!).map((q) => {
												const vals =
													pricing[activeItemId!]?.[q.id] || emptyPricing;
												const tierSug = suggestions.get(q.id);
												const vetterSug =
													tierSug && isVetterSuggestion(tierSug)
														? tierSug
														: undefined;
												return (
													<PriceRowCard
														key={q.id}
														quantity={q.quantity}
														currency={currency}
														options={optionsFor(q.id)}
														values={{
															cost: vals.price,
															salePrice: vals.salePrice,
															markup: vals.markup,
															entryMode: vals.entryMode,
															minDays: vals.minDays,
															maxDays: vals.maxDays,
															sourceQuantity: vals.sourceQuantity,
															sourceVetterEntityId:
																vals.sourceVetterEntityId,
															touched: vals.touched,
															quoted: vals.quoted,
														}}
														onChange={(next) =>
															setPricing((prev) => ({
																...prev,
																[activeItemId!]: {
																	...(prev[activeItemId!] || {}),
																	[q.id]: {
																		...(prev[activeItemId!]?.[
																			q.id
																		] || emptyPricing),
																		price: next.cost,
																		salePrice: next.salePrice,
																		markup: next.markup,
																		entryMode: next.entryMode,
																		minDays: next.minDays,
																		maxDays: next.maxDays,
																		sourceQuantity:
																			next.sourceQuantity,
																		sourceVetterEntityId:
																			next.sourceVetterEntityId,
																		touched: next.touched,
																		// Cleared by any edit: without it the
																		// exact-match rule kept re-filling
																		// figures the admin had typed over.
																		autoApplied:
																			next.autoApplied,
																		quoted: next.quoted,
																	},
																},
															}))
														}
														assignable
														onPick={(option) => {
															/* Picking a supplier's price IS
															   choosing that supplier — otherwise
															   the offer quotes a figure under
															   someone else's name. */
															if (
																option.vetterEntityId &&
																option.vetterEntityId !==
																	vetters[activeItemId!]
															) {
																setVetters((prev) => ({
																	...prev,
																	[activeItemId!]:
																		option.vetterEntityId!,
																}));
															}
															applySuggestion(
																activeItemId!,
																q.id,
																option
															);
														}}
														extra={
															<>
																{vetterSug && (
																	<VetterTierHint
																		sug={vetterSug}
																		// Applied while the cost is still theirs — an
																		// edited cost is the admin's own figure.
																		applied={
																			!!vals.vetterQuote &&
																			vals.price ===
																				String(
																					inFormCurrency(
																						vetterSug,
																						currency
																					)?.baseUnitPrice
																				)
																		}
																		hasSalePrice={
																			!!vals.salePrice
																		}
																		offerCurrency={currency}
																		onUse={() =>
																			applyVetter(
																				activeItemId!,
																				q.id,
																				vetterSug
																			)
																		}
																	/>
																)}
																<TierFeasibility
																	minDays={vals.minDays}
																	maxDays={vals.maxDays}
																	deadline={
																		activeItem?.deadline ??
																		quoteDeadline ??
																		null
																	}
																	acceptDeadline={
																		acceptDeadline ?? null
																	}
																/>
															</>
														}
													/>
												);
											})}
										</div>
									)}
								</div>
							</>
						) : (
							<div className="flex h-full items-center justify-center text-sm text-text-4">
								Select a product
							</div>
						)}
					</div>
				</div>
			</DialogBody>
			<DialogFooter>
				{/* Said out loud, not only on the disabled button: the tier at fault is
				    one of several on a step the admin may have scrolled away from. */}
				{/* Progress, per the design's footer: how far through the step you
				    are, in the unit the step is measured in. It replaces a warning
				    that only appeared once something was already wrong. */}
				<span className="mr-auto text-xs text-text-3">
					{pricedTierCount} of {totalTierCount}{' '}
					{totalTierCount === 1 ? 'quantity' : 'quantities'} priced
					{unpricedTiers.length > 0 && (
						<span className="text-text-3">
							{' · '}
							{unpricedTiers.length} {unpricedTiers.length === 1 ? 'has' : 'have'} no
							client price yet
						</span>
					)}
				</span>
				<Button variant="outline" onClick={onBack}>
					Back
				</Button>
				<Button onClick={onNext}>Next</Button>
			</DialogFooter>
		</div>
	);
}
